import _defineProperty from "reshow-runtime/es/helpers/defineProperty";
import _objectSpread from "reshow-runtime/es/helpers/objectSpread2";
import _slicedToArray from "reshow-runtime/es/helpers/slicedToArray";
import _objectWithoutProperties from "reshow-runtime/es/helpers/objectWithoutProperties";
import React, { useState, useEffect, useReducer, useMemo } from "react";
import { build, SemanticUI } from "react-atomic-molecule";
import callfunc from "call-func";
import { dataStatusKey } from "../../src/const";
var keys = Object.keys;
export var UNMOUNTED = "unmounted";
export var EXITSTART = "exit-start";
export var EXITING = "exiting";
export var EXITED = "exited";
export var ENTERSTART = "enter-start";
export var ENTERING = "entering";
export var ENTERED = "entered";

var reducer = function reducer(state, action) {
  keys(action).forEach(function (key) {
    return state[key] = action[key];
  });
  return state;
};

var getTimeouts = function getTimeouts(timeout) {
  var exit, enter, appear;
  exit = enter = appear = timeout;

  if (timeout != null && typeof timeout !== "number") {
    var _timeout$appear;

    exit = timeout.exit;
    enter = timeout.enter;
    appear = (_timeout$appear = timeout.appear) !== null && _timeout$appear !== void 0 ? _timeout$appear : enter;
  }

  return {
    exit: exit,
    enter: enter,
    appear: appear
  };
};

var setNextCallback = function setNextCallback(callback) {
  var active = true;

  var nextCallback = function nextCallback(event) {
    if (active) {
      callfunc(callback, [event]);
    }

    nextCallback.reset();
  };

  nextCallback.reset = function () {
    active = false;
  };

  return nextCallback;
};

var cancelNextCallback = function cancelNextCallback(state, dispatch) {
  if (state.nextCallback !== null) {
    state.nextCallback.reset();
    dispatch({
      nextCallback: null
    });
  }
};

var perform = function perform(_ref) {
  var step1 = _ref.step1,
      step1Cb = _ref.step1Cb,
      step2 = _ref.step2,
      step2Cb = _ref.step2Cb,
      step3 = _ref.step3,
      step3Cb = _ref.step3Cb,
      setUp = _ref.setUp,
      safeSetState = _ref.safeSetState,
      onTransitionEnd = _ref.onTransitionEnd,
      tearDown = _ref.tearDown,
      goToLast = _ref.goToLast,
      isAppear = _ref.isAppear,
      node = _ref.node,
      timeout = _ref.timeout;

  var last = function last() {
    onTransitionEnd(node, timeout, function () {
      safeSetState(step3, function () {
        callfunc(step3Cb, [node, isAppear]);
        setTimeout(function () {
          return callfunc(tearDown, [node, isAppear]);
        });
      });
    });
  };

  callfunc(setUp, [node, isAppear]);

  if (goToLast) {
    last();
  } else {
    safeSetState(step1, function () {
      callfunc(step1Cb, [node, isAppear]);
      safeSetState(step2, function () {
        callfunc(step2Cb, [node, isAppear]);
        last();
      });
    });
  }
};

var Transition = function Transition(_ref2) {
  var component = _ref2.component,
      children = _ref2.children,
      mountOnEnter = _ref2.mountOnEnter,
      unmountOnExit = _ref2.unmountOnExit,
      appear = _ref2.appear,
      enter = _ref2.enter,
      exit = _ref2.exit,
      timeout = _ref2.timeout,
      addEndListener = _ref2.addEndListener,
      getProps = _ref2.getProps,
      onEnter = _ref2.onEnter,
      onEntering = _ref2.onEntering,
      onEntered = _ref2.onEntered,
      onExit = _ref2.onExit,
      onExiting = _ref2.onExiting,
      onExited = _ref2.onExited,
      resetEntered = _ref2.resetEntered,
      resetExited = _ref2.resetExited,
      statusKey = _ref2.statusKey,
      props = _objectWithoutProperties(_ref2, ["component", "children", "mountOnEnter", "unmountOnExit", "appear", "enter", "exit", "timeout", "addEndListener", "getProps", "onEnter", "onEntering", "onEntered", "onExit", "onExiting", "onExited", "resetEntered", "resetExited", "statusKey"]);

  var _useReducer = useReducer(reducer, {
    "in": null,
    callbackWith: null,
    nextCallback: null,
    init: false,
    node: false
  }),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      dispatch = _useReducer2[1];

  var _useState = useState(function () {
    var thisAppear = appear;
    var initialStatus;

    if (props["in"]) {
      if (thisAppear) {
        initialStatus = EXITED;
      } else {
        initialStatus = ENTERED;
      }
    } else {
      if (unmountOnExit || mountOnEnter) {
        initialStatus = UNMOUNTED;
      } else {
        initialStatus = EXITED;
      }
    }

    return initialStatus;
  }),
      _useState2 = _slicedToArray(_useState, 2),
      status = _useState2[0],
      setStatus = _useState2[1];

  useEffect(function () {
    if (state.callbackWith === status) {
      callfunc(state.nextCallback, [status]);
    }

    var safeSetState = function safeSetState(nextStatus, callback) {
      // This shouldn't be necessary, but there are weird race conditions with
      // setState callbacks and unmounting in testing, so always make sure that
      // we can cancel any pending setState callbacks after we unmount.
      dispatch({
        callbackWith: nextStatus,
        nextCallback: callback ? setNextCallback(callback) : null
      });
      setStatus(nextStatus);
    };

    var onTransitionEnd = function onTransitionEnd(node, timeout, handler) {
      if (state.timer) {
        clearTimeout(state.timer);
      }

      var callback = setNextCallback(function () {
        callfunc(handler);
        callfunc(addEndListener, [{
          node: node,
          state: state,
          status: status
        }]);
      });
      dispatch({
        nextCallback: callback,
        timer: setTimeout(function () {
          return callfunc(state.nextCallback, ["onTransitionEnd"]);
        }, timeout || 0)
      });
    };

    var updateStatus = function updateStatus(mounting, nextStatus) {
      if (nextStatus !== null) {
        // nextStatus will always be ENTERING or EXITING.
        cancelNextCallback(state, dispatch);
        var timeouts = getTimeouts(timeout);

        if (nextStatus === ENTERING) {
          perform({
            step1: ENTERSTART,
            step1Cb: onEnter,
            step2: ENTERING,
            step2Cb: onEntering,
            step3: ENTERED,
            step3Cb: onEntered,
            setUp: resetExited,
            tearDown: resetEntered,
            goToLast: mounting && !appear || !mounting && !enter,
            node: state.node,
            safeSetState: safeSetState,
            onTransitionEnd: onTransitionEnd,
            isAppear: mounting,
            timeout: mounting ? timeouts.appear : timeouts.enter
          });
        } else {
          perform({
            step1: EXITSTART,
            step1Cb: onExit,
            step2: EXITING,
            step2Cb: onExiting,
            step3: EXITED,
            step3Cb: onExited,
            setUp: resetEntered,
            tearDown: resetExited,
            goToLast: !exit,
            node: state.node,
            safeSetState: safeSetState,
            onTransitionEnd: onTransitionEnd,
            timeout: timeouts.exit
          });
        }
      } else if (unmountOnExit && status === EXITED) {
        safeSetState(UNMOUNTED);
      }
    };

    var nextStatus = null;
    var mounting = null;

    if (state["in"] !== props["in"]) {
      mounting = false;
      dispatch({
        "in": props["in"]
      });

      if (props["in"]) {
        if (status !== ENTERING && status !== ENTERED) {
          nextStatus = ENTERING;
        } else if (!state.init) {
          dispatch({
            init: true
          });

          if (appear) {
            nextStatus = ENTERING;
            mounting = true;
          }
        }
      } else {
        if (status === ENTERING || status === ENTERED) {
          nextStatus = EXITING;
        }
      }
    }

    updateStatus(mounting, nextStatus);
    return function () {
      // useEffect clean
      if (state.timer) {
        clearTimeout(state.timer);
      }
    };
  }, [props["in"], status]);
  return useMemo(function () {
    var _objectSpread2;

    var myChild = undefined;

    if (status !== UNMOUNTED) {
      var nextProps = _objectSpread({}, props);

      delete nextProps["in"];
      myChild = build(children)(nextProps);
    }

    return build(component)(_objectSpread((_objectSpread2 = {}, _defineProperty(_objectSpread2, statusKey, status), _defineProperty(_objectSpread2, "refCb", function refCb(el) {
      return dispatch({
        node: el
      });
    }), _objectSpread2), callfunc(getProps, [status]) || {}), myChild);
  }, [props]);
};

Transition.defaultProps = {
  statusKey: dataStatusKey,
  component: SemanticUI,
  "in": false,
  mountOnEnter: false,
  unmountOnExit: false,
  appear: false,
  enter: true,
  exit: true,
  onEnter: null,
  onEntering: null,
  onEntered: null,
  onExit: null,
  onExiting: null,
  onExited: null
};
export default Transition;